import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppSelector } from "hooks";
import UserAvatar from "components/user-avatar/user-avatar";
import {
  CompanyUser,
  fetchProjectUsers,
  shareBoardWithUser,
} from "./share-board-form.service";
import { buildBoardShareUrl, copyTextToClipboard } from "./share-board-link";

// Placeholder rows shown while the member list loads, so the modal opens at its
// final height instead of growing from small → big once the data arrives.
const SKELETON_ROWS = ["s1", "s2", "s3", "s4"];

interface ShareBoardFormProps {
  projectBoardId: string;
}

export default function ShareBoardForm({ projectBoardId }: ShareBoardFormProps) {
  const { t } = useTranslation();
  const { companyId = "" } = useParams();
  const boardName = useAppSelector((s) => s.projectBoardReducer.boardName);

  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const link = buildBoardShareUrl(companyId, projectBoardId);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProjectUsers(companyId)
      .then((list) => {
        if (active) setUsers(list);
      })
      .catch(() => {
        if (active) setUsers([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  const onCopy = async () => {
    const ok = await copyTextToClipboard(link);
    if (ok) {
      setCopied(true);
      toast.success(t("link_copied", { defaultValue: "Link copied" }));
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t("copy_failed", { defaultValue: "Copy failed" }));
    }
  };

  const onSend = async (user: CompanyUser) => {
    if (!user.email || sendingId) return;
    setSendingId(user.id);
    try {
      await shareBoardWithUser(companyId, projectBoardId, user.id);
      toast.success(t("email_sent", { defaultValue: "Email sent" }));
    } catch (e) {
      toast.error(t("email_send_failed", { defaultValue: "Could not send email" }));
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-lg">
      <h2 className="mb-4 text-lg font-semibold text-[var(--p-color-text)]">
        {t("share_board", { defaultValue: "Share board" })}
        {boardName ? (
          <span className="font-normal text-[var(--p-color-text-secondary)]">
            {" — "}
            {boardName}
          </span>
        ) : null}
      </h2>

      {/* Share by link */}
      <div className="mb-6 flex items-center gap-2">
        <input
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface-secondary)] px-3 py-2 text-sm text-[var(--p-color-text)]"
        />
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 rounded-lg bg-[var(--p-color-bg-fill-brand)] px-4 py-2 text-sm font-semibold text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)]"
        >
          {copied
            ? t("link_copied", { defaultValue: "Copied" })
            : t("copy_link", { defaultValue: "Copy link" })}
        </button>
      </div>

      {/* Board members */}
      <div className="mb-1 flex items-center gap-2 border-b border-[var(--p-color-border)] pb-2">
        <span className="text-sm font-semibold text-[var(--p-color-text)]">
          {t("board_members", { defaultValue: "Board members" })}
        </span>
        {!loading && (
          <span className="rounded bg-[var(--p-color-bg-surface-tertiary)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--p-color-text-secondary)]">
            {users.length}
          </span>
        )}
      </div>
      <p className="mb-2 text-xs text-[var(--p-color-text-secondary)]">
        {t("share_access_hint", {
          defaultValue: "Only people who can access the project module are shown.",
        })}
      </p>

      <div className="max-h-80 min-h-[240px] overflow-y-auto">
        {loading ? (
          <ul className="divide-y divide-[var(--p-color-border)]">
            {SKELETON_ROWS.map((k) => (
              <li key={k} className="flex items-center gap-3 py-2.5">
                <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-[var(--p-color-bg-surface-secondary)]" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-3 w-1/3 animate-pulse rounded bg-[var(--p-color-bg-surface-secondary)]" />
                  <div className="h-2.5 w-1/2 animate-pulse rounded bg-[var(--p-color-bg-surface-secondary)]" />
                </div>
                <div className="h-8 w-16 shrink-0 animate-pulse rounded-lg bg-[var(--p-color-bg-surface-secondary)]" />
              </li>
            ))}
          </ul>
        ) : users.length === 0 ? (
          <p className="py-10 text-center text-sm text-[var(--p-color-text-secondary)]">
            {t("share_no_users", { defaultValue: "No users found" })}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--p-color-border)]">
          {users.map((u) => (
            <li key={u.id} className="flex items-center gap-3 py-2.5">
              <UserAvatar
                companyId={companyId}
                user={{ id: u.id, name: u.name, avatarFileId: u.avatarFileId }}
                size={36}
                rounded="full"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[var(--p-color-text)]">
                  {u.name || u.email || "—"}
                </div>
                {u.email && (
                  <div className="truncate text-xs text-[var(--p-color-text-secondary)]">
                    {u.email}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onSend(u)}
                disabled={!u.email || sendingId === u.id}
                className="shrink-0 rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--p-color-text)] hover:bg-[var(--p-color-bg-surface-secondary)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sendingId === u.id
                  ? t("sending", { defaultValue: "Sending…" })
                  : t("send", { defaultValue: "Send" })}
              </button>
            </li>
          ))}
          </ul>
        )}
      </div>
    </div>
  );
}
